import React, { useState, useEffect } from "react";
import { Send, Bell, Mail, CheckCircle } from "lucide-react";
import PremiumModal from "../ui/PremiumModal";
import { esignApi } from "../../services/api";

const SIGNER_STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  pending: { label: "Pending", bg: "bg-slate-100", text: "text-slate-600" },
  sent: { label: "Sent", bg: "bg-blue-100", text: "text-blue-700" },
  viewed: { label: "Viewed", bg: "bg-amber-100", text: "text-amber-700" },
  signed: { label: "Signed", bg: "bg-emerald-100", text: "text-emerald-700" },
  declined: { label: "Declined", bg: "bg-red-100", text: "text-red-700" },
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSent: () => void;
  envelopeId: string;
  signers: any[];
  mode: "resend" | "remind";
}

const ResendReminderModal: React.FC<Props> = ({
  open,
  onClose,
  onSent,
  envelopeId,
  signers,
  mode,
}) => {
  // Filter to only non-signed signers
  const eligibleSigners = signers
    .map((s, i) => ({ ...s, originalIndex: i }))
    .filter((s) => s.status !== "signed");

  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Pre-select all eligible signers when modal opens
  useEffect(() => {
    if (open) {
      const eligible = signers
        .map((s: any, i: number) => ({ ...s, originalIndex: i }))
        .filter((s: any) => s.status !== "signed");
      setSelectedIndices(new Set(eligible.map((s: any) => s.originalIndex)));
      setError("");
    }
  }, [open, signers]);

  const toggleSigner = (index: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedIndices.size === 0) return;
    setSubmitting(true);
    setError("");
    try {
      const apiCall = mode === "resend" ? esignApi.resend : esignApi.remind;
      const promises = Array.from(selectedIndices).map((signerIndex) =>
        apiCall(envelopeId, signerIndex)
      );
      await Promise.all(promises);
      onSent();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          `Failed to ${mode === "resend" ? "resend invitations" : "send reminders"}`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setSelectedIndices(new Set());
    setError("");
    onClose();
  };

  const title = mode === "resend" ? "Resend Invitation" : "Send Reminder";
  const ModalIcon = mode === "resend" ? Send : Bell;
  const actionLabel = mode === "resend" ? "Resend" : "Send Reminder";

  return (
    <PremiumModal
      open={open}
      onClose={handleClose}
      title={title}
      size="sm"
      icon={ModalIcon}
      footer={
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={selectedIndices.size === 0 || submitting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => {
              if (!submitting)
                e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
            }}
            onMouseLeave={(e) => {
              if (!submitting)
                e.currentTarget.style.backgroundColor = "var(--color-primary)";
            }}
          >
            <ModalIcon className="h-4 w-4" />
            {submitting ? "Sending..." : actionLabel}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-500">
          {mode === "resend"
            ? "Select the signers who will receive a new invitation email."
            : "Select the signers who will receive a reminder notification."}
        </p>

        {eligibleSigners.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="p-3 bg-emerald-50 rounded-xl mb-3">
              <CheckCircle className="h-8 w-8 text-emerald-500" />
            </div>
            <p className="text-sm font-medium text-slate-700">
              All signers have signed
            </p>
            <p className="text-xs text-slate-400 mt-1">
              No pending signers to notify
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {eligibleSigners.map((signer) => {
              const isSelected = selectedIndices.has(signer.originalIndex);
              const sStatus =
                SIGNER_STATUS_CONFIG[signer.status] || SIGNER_STATUS_CONFIG.pending;

              return (
                <label
                  key={signer.originalIndex}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                    isSelected
                      ? "border-emerald-200 bg-emerald-50/50"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSigner(signer.originalIndex)}
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800 truncate">
                        {signer.name}
                      </span>
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${sStatus.bg} ${sStatus.text}`}
                      >
                        {sStatus.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                      <Mail className="h-3 w-3" />
                      {signer.email}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {/* Error */}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </PremiumModal>
  );
};

export default ResendReminderModal;
